import { analytics } from "@components/analytics";
import { TicketItem } from "@features/cartDetails/types";
import { BASE_URL, COUNTRY_CODE } from "@utils/constants";
import { getCookieValue } from "@utils/helpers";
import axios from "axios";
import { mutate } from "swr";
import { FormattedPrice } from "../types";
import { GATicket } from "@features/eventPage/types";
import {
  sendGTMTrackingEvent,
  sendIticketGTMTrackingEvent,
} from "@utils/analyticsUtils";
import { Analytics, EventLandingPage } from "@types";
import CartNotificationUpsell from "@features/eventPage/components/cartNotificationUpsell";

async function deleteTicket(ticketsToDelete: TicketItem) {
  const basketKey = getCookieValue("ASP.NET_SessionId");
  const websiteUid = getCookieValue("itk-website-uid");
  try {
    const deletedTicket = await axios.delete(
      `${BASE_URL}/legacy/${COUNTRY_CODE}/shop/basket/${basketKey}/ticket/${ticketsToDelete.UniqueId}`,
      { headers: { "itk-website-uid": websiteUid } }
    );

    return deletedTicket.status;
  } catch (error) {
    return { error };
  }
}

export const handleAddCart = async (
  eventId: number,
  eventName: string,
  eventVenueId: number,
  orgUid: string,
  showingId: number,
  bookedTickets: number | undefined,
  setSuccess: (message?: string, extraContent?: React.ReactElement) => void,
  setFailure: (message: string, isError?: boolean) => void,
  setActionSuccess: (
    message?: string,
    extraContent?: React.ReactElement
  ) => void,
  basketTickets: TicketItem[],
  price: FormattedPrice,
  qty: string,
  connectedShowings: number[] | null,
  isDynamic?: boolean,
  messages?: {
    removedcombo: {
      success: string;
      failure: string;
    };
    removedtickets: {
      success: string;
      failure: string;
    };
    addedtickets: {
      success: string;
      failure: string;
    };
  },
  prev_data?: GATicket,
  analyticsConfigs?: Analytics[],
  landingPages?: EventLandingPage[],
  onAddToCart?: () => void
) => {
  let toBookTickets = parseInt(qty) * 1;
  if (!isDynamic) {
    toBookTickets = toBookTickets - (bookedTickets || 0);
  }

  const basketKey = getCookieValue("ASP.NET_SessionId");
  const websiteUid = getCookieValue("itk-website-uid");

  if (toBookTickets < 0 && price.isCombo) {
    try {
      const deletedTickets: any[] = [];
      for (let i = 0; i < Math.abs(toBookTickets); ++i) {
        const freshBasketTickets =
          i > 0
            ? await axios
                .get(
                  `${BASE_URL}/legacy/${COUNTRY_CODE}/shop/basket/${basketKey}`,
                  {
                    headers: {
                      "itk-website-uid": websiteUid,
                    },
                  }
                )
                .then((res) => res.data)
                .then(
                  (data) =>
                    data.Events.find(
                      (event: any) => event.Id === eventId
                    )?.Showings.find((showing: any) => showing.Id === showingId)
                      ?.Items
                )
            : basketTickets;

        if (!freshBasketTickets) return "failure";

        const ticketToDelete = freshBasketTickets.find(
          (ticket: any) =>
            ticket.ComboId === price.comboId &&
            !deletedTickets.includes(ticket.UniqueId)
        );
        await axios.delete(
          `${BASE_URL}/legacy/${COUNTRY_CODE}/shop/basket/${basketKey}/ticket/${ticketToDelete.UniqueId}`,
          {
            headers: {
              "itk-website-uid": websiteUid,
            },
          }
        );
        deletedTickets.push(ticketToDelete.UniqueId);
      }
      await mutate(
        (key: any) =>
          Array.isArray(key) &&
          key[0].startsWith(
            `events/${eventId}/${eventVenueId}/showings/${showingId}/tickets/ga`
          )
      );
      await mutate(`events/${eventId}/${eventVenueId}/showings`);
      await mutate(`basket/${basketKey}/summary`);
      await mutate(`basket/${basketKey}`);
      await mutate(`basket/${basketKey}/form`);
      setSuccess(
        messages
          ? messages.removedcombo.success
          : "Successfully removed your combo."
      );
      return "success";
    } catch (err) {
      setFailure(
        messages
          ? messages.removedcombo.failure
          : "Something went wrong trying to remove your combo.",
        true
      );
      return "failure";
    }
  }

  if (toBookTickets < 0) {
    const ticket_promises: Promise<any>[] = [];

    for (let i = 0; ticket_promises.length < Math.abs(toBookTickets); ++i) {
      if (
        basketTickets[i].PriceId === price.priceId &&
        basketTickets[i].Combo === null
      ) {
        ticket_promises.push(deleteTicket(basketTickets[i]));
      }
    }

    const deletedTickets = await Promise.all(ticket_promises);

    const allOk = deletedTickets.every((ticket) => ticket === 200);

    if (deletedTickets.length > 0 && allOk) {
      analytics.track("Product Removed", {
        product_id: eventId,
        sku: `${eventId}-${showingId}-${price.priceAgeId}`,
        name: eventName,
        variant: price.type,
        price: price.price,
        quantity: deletedTickets.length,
        currency: price.currency.CurrencyCode,
        value: price.price * deletedTickets.length,
        cart_id: basketKey,
        organisationUid: orgUid,
      });

      const trackingData = {
        event: "remove_from_cart",
        currency: price.currency.CurrencyCode,
        value: price.price * deletedTickets.length,
        items: [
          {
            item_id: eventId,
            sku: `${eventId}-${showingId}-${price.priceAgeId}`,
            item_name: eventName,
            item_variant: price.type,
            price: price.price,
            quantity: deletedTickets.length,
          },
        ],
      };
      sendIticketGTMTrackingEvent(trackingData);
      if (analyticsConfigs) {
        sendGTMTrackingEvent(trackingData, analyticsConfigs);
      }

      if (prev_data) {
        const other_gato = prev_data?.GATicketOptions.filter(
          (to) => to.PriceId !== price.priceId
        );
        const new_gato = prev_data?.GATicketOptions.find(
          (to) => to.PriceId === price.priceId
        );
        const new_data = {
          ...prev_data,
          GATicketOptions: [...(other_gato ?? []), new_gato],
          TicketOptions: [...(other_gato ?? []), new_gato],
        };
        await mutate(
          (key: any) =>
            Array.isArray(key) &&
            key[0].startsWith(
              `events/${eventId}/${eventVenueId}/showings/${showingId}/tickets/ga`
            ),
          new_data
        );
      } else {
        await mutate(
          (key: any) =>
            Array.isArray(key) &&
            key[0].startsWith(
              `events/${eventId}/${eventVenueId}/showings/${showingId}/tickets/ga`
            )
        );
      }

      await mutate(`events/${eventId}/${eventVenueId}/showings`);
      await mutate(`basket/${basketKey}/summary`);
      await mutate(`basket/${basketKey}`);
      await mutate(`basket/${basketKey}/form`);
      setSuccess(
        messages
          ? messages.removedtickets.success
          : "Successfully removed your tickets."
      );
      return "success";
    } else {
      setFailure(
        messages
          ? messages.removedtickets.failure
          : "Something went wrong when trying to remove your tickets.",
        true
      );
      return "failure";
    }
  }

  try {
    const response = price.isCombo
      ? await axios.post(
          `${BASE_URL}/legacy/${COUNTRY_CODE}/shop/basket/${basketKey}/tickets/ga/combo`,
          {
            combos: [
              {
                qty: toBookTickets,
                eventId: eventId,
                eventVenueId: eventVenueId,
                comboId: price.comboId,
                showingId: showingId,
                password: "",
              },
            ],
          },
          {
            headers: {
              "itk-website-uid": websiteUid,
            },
          }
        )
      : await axios.post(
          `${BASE_URL}/legacy/${COUNTRY_CODE}/shop/basket/${basketKey}/tickets/ga`,
          {
            tickets: [
              {
                qty: toBookTickets,
                eventId: eventId,
                eventVenueId: eventVenueId,
                priceSectionId: price.priceSectionId,
                priceAgeId: price.priceAgeId,
                showingId: showingId,
                gaAreaId: price.gaAreaId,
                connectedShowings: connectedShowings,
                password: "",
                ...(isDynamic && {
                  price: price.price,
                }),
              },
            ],
          },
          {
            headers: {
              "itk-website-uid": websiteUid,
            },
          }
        );

    if (!response.data.successfull) {
      await mutate(
        (key: any) =>
          Array.isArray(key) &&
          key[0].startsWith(
            `events/${eventId}/${eventVenueId}/showings/${showingId}/tickets/ga`
          ),
        response.data.gaTickets
      );
      throw new Error(response.data.errors[0]);
    }

    if (response.status >= 200 && response.status < 300) {
      analytics.track("Product Added", {
        product_id: eventId,
        sku: `${eventId}-${showingId}-${price.priceAgeId}`,
        name: eventName,
        variant: price.type,
        price: price.price,
        quantity: toBookTickets,
        currency: price.currency.CurrencyCode,
        value: price.price * toBookTickets,
        cart_id: basketKey,
        organisationUid: orgUid,
      });

      const trackingData = {
        event: "add_to_cart",
        currency: price.currency.CurrencyCode,
        value: price.price * toBookTickets,
        items: [
          {
            item_id: eventId,
            sku: `${eventId}-${showingId}-${price.priceAgeId}`,
            item_name: eventName,
            item_variant: price.type,
            price: price.price,
            quantity: toBookTickets,
          },
        ],
      };
      sendIticketGTMTrackingEvent(trackingData);
      if (analyticsConfigs) {
        sendGTMTrackingEvent(trackingData, analyticsConfigs);
      }

      await mutate(
        (key: any) =>
          Array.isArray(key) &&
          key[0].startsWith(
            `events/${eventId}/${eventVenueId}/showings/${showingId}/tickets/ga`
          ),
        response.data.gaTickets
      );
      await mutate(`events/${eventId}/${eventVenueId}/showings`);
      await mutate(`basket/${basketKey}/summary`);
      await mutate(`basket/${basketKey}`);
      await mutate(`basket/${basketKey}/form`);
      setActionSuccess(
        messages
          ? messages.addedtickets.success
          : "Successfully added ticket to cart.",
        landingPages && landingPages.length > 0 ? (
          <CartNotificationUpsell landingPages={landingPages} />
        ) : undefined
      );
      onAddToCart?.();
      return "success";
    }
  } catch (error: any) {
    if (error?.response?.status === 429) {
      const retry_after = error.response.headers["retry-after"]
        ? parseInt(error.response.headers["retry-after"])
        : error.response.headers["rl-twm"]
        ? Math.round(error.response.headers["rl-twm"] * 60)
        : 5;
      return {
        status: "retry",
        retry_after: retry_after,
      };
    }
    if (
      error?.response &&
      error.response.status >= 400 &&
      error.response.status < 500
    ) {
      setFailure(error.response.data.message);
    } else if (error.message) {
      setFailure(error.message);
    } else {
      setFailure(
        messages ? messages.addedtickets.failure : "Something went wrong.",
        true
      );
    }
    return "failure";
  }
  return "success";
};
